import { useEffect, useMemo, useState } from 'react'
import { sum } from 'lodash'
import { useModals } from 'almanac-ui/Modal'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { Schedule, useAddToBrandPlanSchedule } from 'hooks/queries/schedules/useAddToBrandPlanSchedule'
import { SelectBrandPlanSchedule } from './SelectBrandPlanSchedule'
import { ReviewPostTimes } from './ReviewPostTimes'
import { AddMoreContent } from './AddMoreContent'
import { ConfirmationModal } from '../components/ConfirmationModal'
import { BrandPlan, BrandPlanSchedule } from 'types'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { ApiError } from 'utils/error'
import { generatePath } from 'react-router-dom'
import { BRAND_KITS_PATH } from 'paths'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { MinimalMediaFile } from '../../Types'
import { Additionals, AddMoreContentModalProps, AddMoreContentModalStep } from './Types'
import { Modal } from 'almanac-ui/Modal'
import { useBrandPlanSchedules } from 'hooks/queries/schedules/useBrandPlanSchedules'
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { collectResults } from 'utils/query'
import { TopicAndMedia } from './TopicAndMedia'
import { generateSchedule } from './utils'
import { CreateSelect } from './CreateSelect'
import { useNeedsCreditsToGenerateContent } from 'hooks/useNeedsCreditsToGenerateContent'
import { calculateCreditCounts } from 'utils/calculateCreditCounts'
import { observer } from 'mobx-react-lite'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { usePurchaseContentCreditsModal } from 'hooks/usePurchaseContentCreditsModal'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

dayjs.extend(isSameOrBefore)

const INITIAL_COUNT_STATE: Additionals = {
  instagram_post: 0,
  instagram_reel: 0,
  instagram_story: 0,
  tiktok_post: 0,
  youtube_short: 0,
  facebook_post: 0,
  facebook_story: 0,
  linkedin_post: 0,
  twitter_post: 0,
  twitter_thread: 0,
  newsletter: 0,
  blog_post: 0,
  crosspost_post: 0,
  video_crosspost_post: 0,
  google_my_business_post: 0,
}

export const AddMoreContentModalWrapper = (props: AddMoreContentModalProps) => {
  const { data: brandPlan } = useCurrentBrandPlan()

  if (!brandPlan) return null

  return <AddMoreContentModal {...props} brandPlan={brandPlan} />
}

const AddMoreContentModal = observer(
  ({
    initialTopic,
    initialMediaFiles,
    onSuccess,
    date,
    brandPlan,
    fallbackAction,
    allowWizardsModal = false,
  }: AddMoreContentModalProps & { brandPlan: BrandPlan }) => {
    const effectiveAllowWizardsModal = allowWizardsModal && !brandPlan.has_content_strategies
    let initialStep: AddMoreContentModalStep = 'SelectSchedule'
    if (date) {
      initialStep = 'AddMoreContent'
    }
    if (effectiveAllowWizardsModal) {
      initialStep = 'CreateSelect'
    }
    const [step, setStep] = useState<AddMoreContentModalStep>(initialStep)

    const [targetSchedule, setTargetSchedule] = useState<BrandPlanSchedule>()
    const [topic, setTopic] = useState(initialTopic || '')
    const [schedule, setSchedule] = useState<Schedule[]>([])
    const [additionals, setAdditionals] = useState(INITIAL_COUNT_STATE)
    const [mediaFiles, setMediaFiles] = useState<MinimalMediaFile[]>(initialMediaFiles || [])
    const [excludedAccounts, setExcludedAccounts] = useState<Record<number, boolean>>({})
    const workspaceId = useCurrentWorkspaceId()
    const { openModal, closeModal, closeAllModals } = useModals()
    const { mutate: addMoreContent, isLoading } = useAddToBrandPlanSchedule()
    const isFacebookStoriesEnabled = useFlipperFlag('facebook_stories')
    const { data: externalIntegrationsData } = useExternalIntegrations()
    const schedulesQuery = useBrandPlanSchedules({
      brandPlanId: brandPlan.id,
      start_date: dayjs().format('YYYY-MM-DD'),
      order_direction: 'asc',
    })
    const schedules = collectResults(schedulesQuery, (data) => data.schedules)
    const sortedSchedules = schedules.sort((a, b) => dayjs(a.start_date).diff(dayjs(b.start_date)))

    const scheduleContents = useMemo(() => {
      if (!targetSchedule) return []

      return generateSchedule(
        additionals,
        topic,
        mediaFiles,
        targetSchedule,
        excludedAccounts,
        externalIntegrationsData?.external_integrations || [],
        date,
        isFacebookStoriesEnabled,
      )
    }, [
      additionals,
      topic,
      mediaFiles,
      targetSchedule,
      excludedAccounts,
      externalIntegrationsData,
      date,
      isFacebookStoriesEnabled,
    ])

    const creditCounts = useMemo(() => {
      return calculateCreditCounts(undefined, additionals)
    }, [additionals])
    const { needsCredits, creditsNeeded, autopilotContentCreditsPrice } = useNeedsCreditsToGenerateContent(
      creditCounts.social,
      creditCounts.blog,
      creditCounts.email,
      creditCounts.shortFormVideo,
    )

    const openPurchaseModal = usePurchaseContentCreditsModal()

    const confirmSchedule = () => {
      if (needsCredits && autopilotContentCreditsPrice) {
        openPurchaseModal({ price: autopilotContentCreditsPrice })
        return
      }

      if (!brandPlan || !targetSchedule) {
        showToastMessage(
          'Unable to schedule your content. Try again. Please contact support if the problem persists.',
          'error',
        )
        return
      }

      addMoreContent(
        {
          schedule: scheduleContents,
          brandPlanId: brandPlan.id,
          brandPlanScheduleId: targetSchedule.id,
        },
        {
          onSuccess: () => {
            onSuccess?.(schedule.length)
            closeModal()
          },
          onError: (error) => {
            if (error instanceof ApiError && error.errorCode === 'no_brand_kit_media') {
              const brandKitPath = generatePath(BRAND_KITS_PATH, { workspaceId }) + '#images_videos'
              showToastMessage(
                'Your content preferences require brand kit media, but none are uploaded.',
                'error',
                { type: 'link', to: brandKitPath, label: 'Go to Brand Kit', variant: 'proceed' },
                10,
              )
            } else {
              showErrorMessageToast(error instanceof Error ? error.message : 'An error occurred while adding content.')
            }
          },
        },
      )
    }

    const openConfirmationModal = () => {
      const totalAdditionals = sum(Object.values(additionals))

      if (totalAdditionals > 0 || topic.length > 0) {
        openModal(
          ConfirmationModal,
          {
            onConfirm: closeAllModals,
            onCancel: closeModal,
          },
          {
            newStack: true,
          },
        )
        return
      }

      closeModal()
    }

    useEffect(() => {
      if (!date || !sortedSchedules.length) return

      const dateSchedule = sortedSchedules.find(
        (schedule) =>
          dayjs(schedule.start_date).isSameOrBefore(date, 'day') &&
          dayjs(date).isSameOrBefore(schedule.end_date, 'day'),
      )
      if (dateSchedule) {
        setTargetSchedule(dateSchedule)
      } else {
        closeModal()
        fallbackAction?.()
      }
    }, [date, sortedSchedules, fallbackAction, closeModal])

    if (!brandPlan) return null

    // can't show add more content until a schedule is there
    if (!targetSchedule && step === 'AddMoreContent') {
      return null
    }

    return (
      <Modal.Root onPressOutside={openConfirmationModal}>
        {step === 'CreateSelect' && (
          <CreateSelect
            onNext={() => setStep('SelectSchedule')}
            onWizardsBack={() => {
              setStep('CreateSelect')
              closeModal()
            }}
          />
        )}
        {step === 'SelectSchedule' && (
          <SelectBrandPlanSchedule
            onConfirm={(selectedSchedule) => {
              setTargetSchedule(selectedSchedule)
              setStep('AddMoreContent')
            }}
            onBack={effectiveAllowWizardsModal ? () => setStep('CreateSelect') : undefined}
            schedules={sortedSchedules}
          />
        )}
        {step === 'AddMoreContent' && (
          <AddMoreContent
            targetSchedule={targetSchedule}
            additionals={additionals}
            setAdditionals={setAdditionals}
            excludedAccounts={excludedAccounts}
            setExcludedAccounts={setExcludedAccounts}
            onNext={() => setStep('TopicAndMedia')}
            onBack={date ? undefined : () => setStep('SelectSchedule')}
            needsCredits={needsCredits}
            creditsNeeded={creditsNeeded}
          />
        )}
        {step === 'TopicAndMedia' && (
          <TopicAndMedia
            topic={topic}
            setTopic={setTopic}
            mediaFiles={mediaFiles}
            setMediaFiles={setMediaFiles}
            onNext={() => setStep('ReviewPostTimes')}
            onBack={() => {
              setStep('AddMoreContent')
            }}
          />
        )}
        {step === 'ReviewPostTimes' && targetSchedule && (
          <ReviewPostTimes
            onBack={() => {
              setSchedule([])
              setStep('TopicAndMedia')
            }}
            onConfirm={confirmSchedule}
            schedule={scheduleContents}
            setSchedule={setSchedule}
            targetSchedule={targetSchedule}
            isLoading={isLoading}
            selectedBlogPlatform={brandPlan?.selected_blog_platform}
            disableDatePicker={!!date}
            needsCredits={needsCredits}
            creditsNeeded={creditsNeeded}
          />
        )}
      </Modal.Root>
    )
  },
)

export { AddMoreContentModalWrapper as AddMoreContentModal }
